import React, { useEffect, useState, useCallback, useMemo, useRef } from "react"
import { useMap } from "react-leaflet"
import {
  Paper,
  IconButton,
  Typography,
  Button,
  Box,
  Divider,
  Snackbar,
  Portal,
} from "@mui/material"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import CloseIcon from "@mui/icons-material/Close"
import * as turf from "@turf/turf"
import { computeTotalConsumption } from "@/utils/RouteProcessing"
import { nowMs } from "@/utils/appNow"
import VisibilitySwitch from "@/components/ui/VisibilitySwitch"
import NotificationSnackbar from "@/components/ui/NotificationSnackbar"

import WizardStepNav from "@/features/voyagePlanner/wizard/WizardStepNav"
import WizardFooter from "@/features/voyagePlanner/wizard/WizardFooter"
import PlanStep from "@/features/voyagePlanner/wizard/steps/PlanStep"
import OptionsStep from "@/features/voyagePlanner/wizard/steps/OptionsStep"
import ResultStep from "@/features/voyagePlanner/wizard/steps/ResultStep"
import useWizardState from "@/features/voyagePlanner/hooks/useWizardState"
import {
  buildFuelRequestParams,
  hasUsableConsumptionCurve,
} from "@/features/voyagePlanner/fuelRequestParams"
import useSpecificationsDraft from "@/features/voyagePlanner/hooks/useSpecificationsDraft"
import useSpecVessel from "@/features/voyagePlanner/hooks/useSpecVessel"
import useVesselConsumptionProfile from "@/features/voyagePlanner/hooks/useVesselConsumptionProfile"
import useVesselSpeedProfile from "@/features/voyagePlanner/hooks/useVesselSpeedProfile"
import { positionAlongRoutePointFeatures } from "@/features/voyagePlanner/departureFallbackPosition"
import usePlannerVoyage from "@/features/voyagePlanner/hooks/usePlannerVoyage"
import useRouteError from "@/features/voyagePlanner/hooks/useRouteError"
import useManualSketch from "@/features/voyagePlanner/hooks/useManualSketch"
import { routeErrorAdjustStep } from "@/features/voyagePlanner/routeError"
import useVesselOptions from "@/features/vessel/hooks/useVesselOptions"
import usePanelWidth from "@/hooks/usePanelWidth"
import PanelResizeHandle from "@/components/ui/PanelResizeHandle"
import {
  PANEL_TITLE_SX,
  PANEL_SUBTITLE_SX,
  PANEL_SCROLLBAR_SX,
  PANEL_HEADER_DIVIDER_SX,
} from "@/theme/panelStyles"
import {
  isDepartureValid,
  mergeVesselMetaIntoSettings,
} from "@/features/voyagePlanner/voyageSettingsFields"
import { onMouseEnter, onMouseLeave } from "@/utils/mapUx"
import { formatLatLngDM } from "@/utils/geoFormat"
import { checkAndRefreshToken } from "@/services/auth"
import { useAccessContext } from "@/contexts/AccessContext"
import usePortsGeojson from "@/hooks/usePortsGeojson"
import runPlanrouteAnalyser from "@/features/voyagePlanner/runPlanrouteAnalyser"
import Pathfinder from "@/features/voyagePlanner/Pathfinder"
import {
  useIsCalculating,
  useRenderableRoute,
  useRouteStore,
} from "@/features/route/state/RouteStore.jsx"
import {
  isValidLonLat,
  selectDraftRouteIdForVessel,
  selectNextDraftRouteId,
} from "@/features/route/state/RouteSelectors.jsx"
import { createDraftDiscardState } from "@/features/route/state/RouteState.jsx"
import { togglePanel } from "@/reducers/panelsReducer"
import { useLayoutBounds } from "@/features/layout/layoutBounds.jsx"
import { TourHelpButton } from "@/features/tutorials"

dayjs.extend(utc)

const COORD_EPSILON = 1e-6

// Display formatter — uses the shared geoFormat helper so DDM output and
// longitude normalisation stay consistent across the app.
const formatCoordPair = formatLatLngDM

// Accept either "52.48,-31.64" (decimal) or "52° 28.8' N, 31° 38.4' W" (DDM).
// Returns a finite number, or null on failure.
function parseCoordPart(text) {
  if (!text) return null
  const trimmed = String(text).trim()
  // DDM with optional symbols and direction suffix
  const m = trimmed.match(
    /^(-?\d+(?:\.\d+)?)\s*[°°]?\s*(\d+(?:\.\d+)?)?\s*[′']?\s*([NSEW])?$/i,
  )
  if (m) {
    const deg = parseFloat(m[1])
    const min = m[2] ? parseFloat(m[2]) : 0
    const dir = (m[3] || "").toUpperCase()
    if (Number.isFinite(deg) && Number.isFinite(min)) {
      let decimal = Math.abs(deg) + min / 60
      if (deg < 0 || dir === "S" || dir === "W") decimal = -decimal
      return decimal
    }
  }
  // Plain number fallback
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : null
}

function coordinatesMatch(a, b) {
  if (
    !Array.isArray(a) ||
    !Array.isArray(b) ||
    a.length !== 2 ||
    b.length !== 2
  ) {
    return false
  }
  return (
    Math.abs(Number(a[0]) - Number(b[0])) < COORD_EPSILON &&
    Math.abs(Number(a[1]) - Number(b[1])) < COORD_EPSILON
  )
}

/**
 * Voyage planner side panel: a 3-step wizard to compute (Pathfinder/ETA) and
 * save vessel routes. App-bound — must be mounted inside a react-leaflet
 * MapContainer, AccessContextProvider and RouteStoreProvider, with its state
 * props wired from the host app; it is not a standalone widget.
 */
function VoyagePlannerPanel({
  isOpen,
  dispatch,
  voyagePlannerPoints,
  setVoyagePlannerPoints,
  setPlannerDraftSlot,
  plannerVesselSettings,
  setPlannerVesselSettings,
  switchVoyagePlannerActive,
  setSwitchVoyagePlannerActive,
  voyageActive,
  setVoyageActive,
  calculatorBasespeed,
  setCalculatorBasespeed,
  profileOptions,
  etaResponse,
  setEtaResponse,
  routeConsumption,
  setRouteConsumption,
  cwPortGeojson,
  setCwPortGeojson,
  startInput,
  setStartInput,
  startEndpoint,
  setStartEndpoint,
  startError,
  setStartError,
  finishInput,
  setFinishInput,
  finishEndpoint,
  setFinishEndpoint,
  finishError,
  setFinishError,
  calculatorDepartureTime,
  setCalculatorDepartureTime,
  defaultPlannerVesselSettings,
  speedProfileData,
  setSpeedProfileData,
  consumptionProfileData,
  setConsumptionProfileData,
  cwViaAreasGeojson,
  setToogleButtonsOnOff,
  switchManualPathing,
  plannerRoutingMode,
  setPlannerRoutingMode,
  pathfinderResponse,
  setPathfinderResponse,
  etaError,
  setEtaError,
  applyRnwSafety,
  setApplyRnwSafety,
  applyEca,
  setApplyEca,
  avoidHra,
  avoidJwla,
  avoidCaa,
  setAvoidHra,
  setAvoidJwla,
  setAvoidCaa,
  bunkerPrices,
  setBunkerPrices,
  currentDraft,
  setCurrentDraft,
  canSeeErklaerbaer,
  erklaerbaerEnabled,
  setErklaerbaerEnabled,
  erklaerbaerId,
  setErklaerbaerId,
  draftPlannerVesselSettings,
  setDraftPlannerVesselSettings,
  selectedProfile,
  setSelectedProfile,
  lastPositionByVesselId,
  voyageRefreshSignal,
  markedVesselNames,
  setMarkedVesselNames,
  recenterToVesselLastPosition,
  recenterToPoint,
  onGoToVesselPanel,
  newVoyageVesselId = null,
}) {
  const { accessContext, setAccessContext } = useAccessContext()
  // `replaceRoute` lives in the RouteStore context that <RouteStoreProvider>
  // mounts in Application.jsx. Reading it here is purely additive — we just
  // pass it through to Pathfinder / runPlanrouteAnalyser alongside the existing
  // setters.
  const {
    state: routeStoreState,
    replaceRoute,
    setIsCalculating,
  } = useRouteStore()
  const isCalculating = useIsCalculating()

  const map = useMap()

  const { wizardStep, specTab, setSpecTab, goToStep } = useWizardState()

  // Draft-copy lifecycle of the Options step (copy on enter, validate +
  // commit on leave) — shared with the RNW analyser's specifications view.
  const {
    optionsErrors,
    setOptionsError,
    enterSpecs,
    commitIfValid,
  } = useSpecificationsDraft({
    plannerVesselSettings,
    setPlannerVesselSettings,
    draftPlannerVesselSettings,
    setDraftPlannerVesselSettings,
  })
  const [optionsBlockedOpen, setOptionsBlockedOpen] = useState(false)

  // Defer heavy Autocomplete option rendering until the field is opened
  const [startAutoOpen, setStartAutoOpen] = useState(false)
  const [finishAutoOpen, setFinishAutoOpen] = useState(false)

  const [loadingCalcButton, setLoadingCalcButton] = useState(false)

  // erklaerbaerId is only set when the automatic window.open was popup-blocked
  const [erklaerbaerBlockedOpen, setErklaerbaerBlockedOpen] = useState(false)
  useEffect(() => {
    if (erklaerbaerId) setErklaerbaerBlockedOpen(true)
  }, [erklaerbaerId])
  const handleOpenBlockedErklaerbaer = () => {
    const base = accessContext?.environment?.core_api?.url
    if (base) {
      window.open(
        `${base}/services/erklaerbaer/${erklaerbaerId}`,
        "_blank",
        "noopener,noreferrer",
      )
    }
    setErklaerbaerId(null)
    setErklaerbaerBlockedOpen(false)
  }

  const [basespeedError, setBasespeedError] = useState(false)
  const plannerVesselOptions = useVesselOptions()
  const [plannerSelectedVessel, setPlannerSelectedVessel] = useState(null)
  const {
    voyage: plannerSelectedVoyage,
    voyagesLoaded: plannerVoyagesLoaded,
    voyagesFailed: plannerVoyagesFailed,
    activeRouteId: plannerActiveRouteId,
    activeRoutePoints,
  } = usePlannerVoyage({
    vesselId: plannerSelectedVessel?.id,
    enabled: isOpen,
    refreshSignal: voyageRefreshSignal,
  })
  // Opened via a new-voyage card, or the vessel has no active voyage: drafts carry no voyage.
  const startsNewVoyage =
    (newVoyageVesselId != null &&
      Number(newVoyageVesselId) === Number(plannerSelectedVessel?.id)) ||
    (plannerVoyagesLoaded && !plannerSelectedVoyage)
  const draftVoyage = startsNewVoyage ? null : plannerSelectedVoyage
  const bounds = useLayoutBounds()
  const panelMaxHeight = bounds.panelHeight

  const panelRef = useRef(null)
  const { panelWidth, handleResizeMouseDown } = usePanelWidth({
    storageKey: "voyagePlannerPanelWidth",
    map,
    panelRef,
  })
  const autoFilledStartVesselIdRef = useRef(null)
  // {routeId, text} of the last arrival autofill; a differing field text means the user owns the finish.
  const lastArrivalFillRef = useRef(null)
  const endpointSyncSourceRef = useRef(null)
  const startEndpointRef = useRef(null)
  const finishEndpointRef = useRef(null)

  // No departure time: the default "now" auto-advances every minute.
  const routeInputsKey = JSON.stringify([
    (voyagePlannerPoints?.features ?? []).map((f) => [
      f?.geometry?.coordinates,
      f?.properties?.legtype,
    ]),
    calculatorBasespeed,
    plannerVesselSettings,
    switchManualPathing,
    applyRnwSafety,
    applyEca,
    avoidHra,
    avoidJwla,
    avoidCaa,
  ])
  const {
    routeError,
    setRouteError,
    clearRouteError,
    toastOpen: routeErrorToastOpen,
    closeToast: closeRouteErrorToast,
  } = useRouteError({ inputsKey: routeInputsKey })

  // Extract route_summary from pathfinder response for status/warnings display
  const routeSummary = useMemo(() => {
    if (!pathfinderResponse || pathfinderResponse.type !== "FeatureCollection")
      return null
    const summaryFeature = (pathfinderResponse.features ?? []).find(
      (f) => f?.properties?.type === "route_summary",
    )
    return summaryFeature?.properties ?? null
  }, [pathfinderResponse])

  // Extract port options with optional chaining (to be safe if some properties are undefined)
  const portOptions = useMemo(
    () => cwPortGeojson?.features || [],
    [cwPortGeojson],
  )
  const isStartValid = startInput !== "" && !startError
  const isFinishValid = finishInput !== "" && !finishError

  const hasSelectedVessel = Number.isFinite(Number(plannerSelectedVessel?.id))

  // Vessel master data for the spec views and the compute-time request merge.
  // There is no default vessel: computing requires real vessel dimensions,
  // merged from the vessel meta at request time.
  const specVesselId = Number(
    selectedProfile?.vesselId ?? plannerSelectedVessel?.id,
  )
  const { specVesselMeta, mergedSettings, vesselDataComplete } = useSpecVessel({
    vesselId: specVesselId,
    plannerVesselSettings,
  })

  // Vessel-specific consumption profile (incl. referenceDraft/draftDependency
  // from the ShippingDB) — same vessel resolution as the spec views.
  useVesselConsumptionProfile({
    vesselId: specVesselId,
    setConsumptionProfileData,
    currentDraft,
    setCurrentDraft,
  })

  // Vessel-specific wind/wave speed profiles — same vessel resolution.
  useVesselSpeedProfile({
    vesselId: specVesselId,
    setSpeedProfileData,
  })

  // Reasons the compute button is disabled — shown as a footer hint so a
  // greyed-out "Find Route" isn't a dead end.
  const endpointsPlaced = voyagePlannerPoints.features.length > 1
  const usableConsumptionProfile = hasUsableConsumptionCurve(
    consumptionProfileData?.values,
  )
  const missingRequirements = [
    !hasSelectedVessel && "Select a vessel",
    hasSelectedVessel &&
      !vesselDataComplete &&
      "Vessel data is incomplete — edit vessel data in Options",
    !isStartValid && "Set a valid departure point",
    !isFinishValid && "Set a valid arrival point",
    // Only surface the map hint when the inputs look valid but the points
    // aren't placed yet — otherwise it just repeats the point messages above.
    isStartValid &&
      isFinishValid &&
      !endpointsPlaced &&
      "Place start and finish on the map",
    calculatorBasespeed === "" && "Enter a base speed",
    !isDepartureValid(calculatorDepartureTime) &&
      "Set a departure time that is not in the past",
    !(Number(bunkerPrices?.usdPerTon) > 0) &&
      "Enter a bunker price (Voyage tab)",
    !(Number(bunkerPrices?.ecaUsdPerTon) > 0) &&
      "Enter an ECA bunker price (Voyage tab)",
    plannerRoutingMode === "fuel" &&
      !usableConsumptionProfile &&
      "Fuel optimisation needs a consumption profile for the vessel",
    Number(consumptionProfileData?.referenceDraft) > 0 &&
      Number(consumptionProfileData?.draftDependency) > 0 &&
      !(Number(currentDraft) > 0) &&
      "Enter the current draft (Consumption tab)",
  ].filter(Boolean)
  // Manual routes aren't routed: RNW has no effect; ECA and zones only flag and cost.
  const restrictionFlags = {
    applyRnwSafety: switchManualPathing ? false : applyRnwSafety,
    applyEca,
    avoidHra,
    avoidJwla,
    avoidCaa,
  }
  const isButtonEnabled = missingRequirements.length === 0

  usePortsGeojson({
    enabled: isOpen,
    cwPortGeojson,
    setCwPortGeojson,
    errorContext: ".core /geo/ports (voyage planner) error=",
    errorLabel: "Failed to load port options for voyage planner:",
  })

  const accountId = accessContext?.payload?.accountId
  const draftVesselLabel = `${plannerSelectedVessel?.name ?? "Planner"} · Planner`

  // One draft per vessel: recompute replaces in place, else allocate.
  const plannerDraftRouteId = useMemo(
    () => selectDraftRouteIdForVessel(routeStoreState, plannerSelectedVessel?.id),
    [routeStoreState, plannerSelectedVessel?.id],
  )
  const draftRenderable = useRenderableRoute(plannerDraftRouteId)
  const draftHasPoints =
    (draftRenderable?.points?.geojson?.features?.length ?? 0) > 0

  const buildDraftTarget = () => ({
    draftRouteId:
      plannerDraftRouteId ?? selectNextDraftRouteId(routeStoreState),
    draftContext: {
      vesselId: plannerSelectedVessel?.id ?? null,
      vesselName: plannerSelectedVessel?.name ?? null,
      voyageId: draftVoyage?.id ?? null,
      voyageName: draftVoyage?.name ?? null,
      startPort: getPortNameById(startEndpoint?.portId),
      finishPort: getPortNameById(finishEndpoint?.portId),
    },
  })

  const {
    manualMiddles,
    startLegtype,
    isSketch,
    showSketch,
    discardSketch,
  } = useManualSketch({
    enabled: switchManualPathing,
    startEndpoint,
    finishEndpoint,
    vesselId: plannerSelectedVessel?.id,
    draftRouteId: plannerDraftRouteId,
    draftRenderable,
    routeStoreState,
    replaceRoute,
    buildDraftTarget,
    vesselLabel: draftVesselLabel,
  })

  // The map layers need the planner's slot: sketch rendering and recalc ownership.
  // Plan/Options edit the geometry: a computed manual draft shows as its sketch there.
  const sketchView = switchManualPathing && wizardStep < 2
  useEffect(() => {
    setPlannerDraftSlot((prev) =>
      prev.routeId === plannerDraftRouteId &&
      prev.showSketch === showSketch &&
      prev.sketchView === sketchView
        ? prev
        : { routeId: plannerDraftRouteId, showSketch, sketchView },
    )
  }, [plannerDraftRouteId, showSketch, sketchView, setPlannerDraftSlot])

  const hasVesselLastPosition = useMemo(() => {
    const vesselId = Number(plannerSelectedVessel?.id)
    if (!Number.isFinite(vesselId)) return false
    const lonlat = lastPositionByVesselId?.[vesselId]
    if (!Array.isArray(lonlat) || lonlat.length !== 2) return false
    const [lon, lat] = lonlat
    return Number.isFinite(Number(lon)) && Number.isFinite(Number(lat))
  }, [plannerSelectedVessel?.id, lastPositionByVesselId])

  const validateCoords = useCallback((coords) => {
    if (typeof coords !== "string") return false
    const parts = coords.split(",")
    if (parts.length !== 2) return false
    return parts.every((part) => parseCoordPart(part) !== null)
  }, [])

  const parseCoordsInput = useCallback(
    (value) => {
      if (!validateCoords(value)) return null
      const [latRaw, lngRaw] = value.split(",")
      const lat = parseCoordPart(latRaw)
      const lng = parseCoordPart(lngRaw)
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
      return { lat, lng }
    },
    [validateCoords],
  )

  const buildEndpoint = useCallback(({ lng, lat, portId, text }) => {
    const numLng = Number(lng)
    const numLat = Number(lat)
    if (!Number.isFinite(numLng) || !Number.isFinite(numLat)) {
      return null
    }
    // Storage keeps lng as-clicked so the marker renders where the user
    // dropped it (Leaflet positions in the viewport's world copy). The
    // displayed text is normalised separately by formatCoordPair.
    return {
      coordinates: [numLng, numLat],
      portId: portId ?? null,
      text: text ?? formatCoordPair(numLng, numLat),
    }
  }, [])

  const updateStartPoint = useCallback(
    (lat, lng, portId, text) => {
      setStartEndpoint(
        buildEndpoint({
          lat,
          lng,
          portId,
          text: text ?? formatCoordPair(lng, lat),
        }),
      )
    },
    [buildEndpoint],
  )

  const updateFinishPoint = useCallback(
    (lat, lng, portId, text) => {
      setFinishEndpoint(
        buildEndpoint({
          lat,
          lng,
          portId,
          text: text ?? formatCoordPair(lng, lat),
        }),
      )
    },
    [buildEndpoint],
  )

  const applyStartCoords = useCallback(
    (lon, lat) => {
      const nextText = formatCoordPair(lon, lat)
      setStartInput(nextText)
      setStartError(false)
      updateStartPoint(lat, lon, -1, nextText)
      return nextText
    },
    [setStartInput, setStartError, updateStartPoint],
  )

  const applyFinishCoords = useCallback(
    (lon, lat) => {
      const nextText = formatCoordPair(lon, lat)
      setFinishInput(nextText)
      setFinishError(false)
      updateFinishPoint(lat, lon, undefined, nextText)
      return nextText
    },
    [setFinishInput, setFinishError, updateFinishPoint],
  )

  // {vesselId, text} of the last fallback fill; a differing field text means
  // someone else (user, reported, restore) owns the start now.
  const lastFallbackFillRef = useRef(null)

  const applyStartFromVesselLastPosition = useCallback(
    (vesselId) => {
      const parsedVesselId = Number(vesselId)
      if (!Number.isFinite(parsedVesselId)) return false
      const lonlat = lastPositionByVesselId?.[parsedVesselId]
      if (!Array.isArray(lonlat) || lonlat.length !== 2) return false
      const [lon, lat] = lonlat
      if (!Number.isFinite(Number(lon)) || !Number.isFinite(Number(lat))) {
        return false
      }
      applyStartCoords(lon, lat)
      autoFilledStartVesselIdRef.current = parsedVesselId
      lastFallbackFillRef.current = null
      return true
    },
    [lastPositionByVesselId, applyStartCoords],
  )

  // No reported position: {vesselId, baseline} — fulfilled by the effect below
  // once the route's points are in the store; dropped if the start text moved
  // away from the baseline meanwhile (someone else wrote it).
  const [startFallbackRequest, setStartFallbackRequest] = useState(null)
  const startInputRef = useRef(startInput)
  startInputRef.current = startInput
  const applyStartFromVesselPosition = useCallback(
    (vesselId) => {
      const parsedVesselId = Number(vesselId)
      if (!Number.isFinite(parsedVesselId)) return
      if (applyStartFromVesselLastPosition(parsedVesselId)) return
      setStartFallbackRequest({
        vesselId: parsedVesselId,
        baseline: startInputRef.current ?? "",
      })
    },
    [applyStartFromVesselLastPosition],
  )

  useEffect(() => {
    const vesselId = Number(plannerSelectedVessel?.id)
    if (!Number.isFinite(vesselId)) return
    const current = startInput ?? ""
    const request =
      startFallbackRequest?.vesselId === vesselId ? startFallbackRequest : null
    if (startFallbackRequest && (!request || current !== request.baseline)) {
      setStartFallbackRequest(null)
      return
    }
    const fill = lastFallbackFillRef.current
    const owned = fill?.vesselId === vesselId && current === fill.text
    if (!request && !owned) return
    // A reported position that landed meanwhile wins over the fallback.
    if (request && autoFilledStartVesselIdRef.current === vesselId) {
      setStartFallbackRequest(null)
      return
    }
    const features = activeRoutePoints?.features
    if (!(features?.length > 0)) return
    const atMs = calculatorDepartureTime?.isValid?.()
      ? calculatorDepartureTime.valueOf()
      : nowMs()
    const lonlat = positionAlongRoutePointFeatures(features, atMs)
    if (Array.isArray(lonlat)) {
      const text = formatCoordPair(lonlat[0], lonlat[1])
      // Deliberately not marking autoFilledStartVesselIdRef: a reported
      // position arriving later must still override the route fallback.
      if (text !== current) applyStartCoords(lonlat[0], lonlat[1])
      lastFallbackFillRef.current = { vesselId, text }
    }
    if (request) setStartFallbackRequest(null)
  }, [
    activeRoutePoints,
    calculatorDepartureTime,
    plannerSelectedVessel?.id,
    startInput,
    startFallbackRequest,
    applyStartCoords,
  ])

  // Mirror the globally selected vessel into the planner. Resolve by id, else by
  // name (map-icon clicks carry no id).
  useEffect(() => {
    if (!isOpen) return
    if (!(plannerVesselOptions ?? []).length) return

    const marked = (markedVesselNames ?? [])[0]
    const markedVesselId = Number(marked?.vesselId)
    const markedVesselName = marked?.vesselName ?? ""

    const matched = Number.isFinite(markedVesselId)
      ? (plannerVesselOptions ?? []).find(
          (option) => Number(option?.id) === markedVesselId,
        )
      : markedVesselName
        ? (plannerVesselOptions ?? []).find(
            (option) => (option?.name ?? "") === markedVesselName,
          )
        : null

    // No (resolvable) vessel selected → leave the picker empty; a real vessel
    // must be chosen before a route can be calculated.
    if (!matched || matched.id == null) {
      setPlannerSelectedVessel((prev) => (prev?.id == null ? null : prev))
      return
    }

    // Already in sync → nothing to do (avoids re-triggering downstream effects).
    if (Number(plannerSelectedVessel?.id) === Number(matched.id)) return

    // The side effects below update parent-owned state. They must run in the
    // effect body, not inside a setPlannerSelectedVessel updater — updater
    // callbacks run during render, and setting another component's state from
    // there triggers React's "cannot update a component while rendering a
    // different component" warning.
    setPlannerSelectedVessel(matched)
    if (!switchManualPathing) setApplyRnwSafety(true)
    applyStartFromVesselPosition(Number(matched.id))
  }, [
    isOpen,
    markedVesselNames,
    plannerVesselOptions,
    plannerSelectedVessel?.id,
    switchManualPathing,
    setApplyRnwSafety,
    applyStartFromVesselPosition,
  ])

  // Keep specifications profile aligned with planner-selected vessel.
  useEffect(() => {
    const vesselId = Number(plannerSelectedVessel?.id)
    if (!Number.isFinite(vesselId)) {
      setSelectedProfile(profileOptions?.[0] ?? null)
      return
    }
    const matchedProfile =
      (profileOptions ?? []).find(
        (profile) => Number(profile?.vesselId) === vesselId,
      ) ?? null
    setSelectedProfile(matchedProfile)
  }, [plannerSelectedVessel?.id, profileOptions, setSelectedProfile])

  // Arrival default: last point of the active route.
  const applyArrivalFromRoute = useCallback(() => {
    const coords = activeRoutePoints?.features?.at(-1)?.geometry?.coordinates
    if (!isValidLonLat(coords)) return false
    const text = applyFinishCoords(Number(coords[0]), Number(coords[1]))
    lastArrivalFillRef.current = { routeId: plannerActiveRouteId, text }
    return true
  }, [activeRoutePoints, plannerActiveRouteId, applyFinishCoords])

  // A vessel switch re-arms the arrival default even over a user-typed finish
  // (the previous vessel's arrival means nothing for the new one); same
  // {vesselId, baseline} shape as the start fallback.
  const [arrivalRequest, setArrivalRequest] = useState(null)
  const finishInputRef = useRef(finishInput)
  finishInputRef.current = finishInput
  useEffect(() => {
    const vesselId = Number(plannerSelectedVessel?.id)
    setArrivalRequest(
      Number.isFinite(vesselId)
        ? { vesselId, baseline: finishInputRef.current ?? "" }
        : null,
    )
  }, [plannerSelectedVessel?.id])

  // Otherwise follows the active route only while the finish is empty or still holds the last autofill.
  useEffect(() => {
    if (!isOpen || plannerActiveRouteId == null) return
    const current = finishInput ?? ""
    const request =
      arrivalRequest?.vesselId === Number(plannerSelectedVessel?.id)
        ? arrivalRequest
        : null
    if (arrivalRequest && (!request || current !== request.baseline)) {
      setArrivalRequest(null)
      return
    }
    if (!request) {
      const fill = lastArrivalFillRef.current
      if (fill?.routeId === plannerActiveRouteId) return
      if (current !== "" && current !== fill?.text) return
    }
    if (applyArrivalFromRoute() && request) setArrivalRequest(null)
  }, [
    isOpen,
    plannerActiveRouteId,
    plannerSelectedVessel?.id,
    finishInput,
    arrivalRequest,
    applyArrivalFromRoute,
  ])

  // mutates start/finish: sets start from selected vessel's last known position.
  useEffect(() => {
    const vesselId = Number(plannerSelectedVessel?.id)
    if (!Number.isFinite(vesselId)) {
      autoFilledStartVesselIdRef.current = null
      return
    }
    if (autoFilledStartVesselIdRef.current === vesselId) return
    applyStartFromVesselPosition(vesselId)
  }, [plannerSelectedVessel?.id, applyStartFromVesselPosition])

  // Route output (ETA/fuel/warnings) belongs to the last compute; clear it on
  // vessel switch so it can't show the previous vessel's numbers, then
  // reconstruct it from the new vessel's draft so the card isn't empty.
  const outputVesselIdRef = useRef(plannerSelectedVessel?.id ?? null)
  useEffect(() => {
    const vesselId = plannerSelectedVessel?.id ?? null
    const vesselChanged = outputVesselIdRef.current !== vesselId
    if (vesselChanged) {
      outputVesselIdRef.current = vesselId
      setEtaResponse(false)
      setRouteConsumption(false)
      setPathfinderResponse(null)
      setEtaError(null)
      clearRouteError()
    }

    const rawResponse = draftRenderable?.rawResponse
    const features = draftRenderable?.points?.geojson?.features ?? []
    if (!rawResponse || features.length === 0) return
    // The closure still sees the pre-clear response on the switch run.
    const currentResponse = vesselChanged ? null : pathfinderResponse
    // After a real compute both hold the same object; a differing one is a
    // newer compute that owns the output.
    if (currentResponse && currentResponse !== rawResponse) return

    setPathfinderResponse(rawResponse)
    const lastTimestamp = features[features.length - 1]?.properties?.timestamp
    if (lastTimestamp) {
      setEtaResponse(dayjs.utc(lastTimestamp).format("YYYY-MM-DD HH:mm:ss"))
    }
    // Re-runs when the vessel's consumption profile finishes loading.
    const consumption = computeTotalConsumption(
      rawResponse,
      calculatorBasespeed,
      consumptionProfileData,
    )
    if (consumption) setRouteConsumption(consumption)
  }, [
    plannerSelectedVessel?.id,
    draftRenderable,
    pathfinderResponse,
    calculatorBasespeed,
    consumptionProfileData,
    setEtaResponse,
    setRouteConsumption,
    setPathfinderResponse,
    setEtaError,
  ])

  // Handler for when the "Close VoyagePlanner Panel" button is clicked.
  const handleClose = useCallback(() => {
    dispatch(togglePanel("voyagePlanner"))
    onMouseLeave(map)
  }, [dispatch])

  // Helper to get a port's name.
  const getPortLabel = useCallback((option) => {
    // If the option is a string (e.g., freeSolo input), return it as-is.
    if (typeof option === "string") return option

    // Get the port name (or fallback if missing)
    const name = option.properties?.name || "Unknown port"
    const terminal = option.properties?.terminal
    const berth = option.properties?.berth

    // Get the unlocode property.
    const unlocode = option.properties?.unlocode
    // If country exists, return "Name (Country)", otherwise just return name.
    return unlocode ? `${name} (${unlocode}) ${terminal} ${berth}` : name
  }, [])

  // Port name + UN/LOCODE without terminal/berth, for voyage name suggestions.
  const getPortNameById = useCallback(
    (portId) => {
      if (!(Number(portId) > 0) || !Array.isArray(cwPortGeojson?.features)) return null
      const props = cwPortGeojson.features.find(
        (feature) => String(feature?.properties?.cw_id) === String(portId),
      )?.properties
      const name = props?.name?.trim()
      if (!name) return null
      return { name, code: props?.unlocode?.trim()?.toUpperCase() || null }
    },
    [cwPortGeojson],
  )

  const getPortDisplayLabelById = useCallback(
    (portId) => {
      if (portId == null || !Array.isArray(cwPortGeojson?.features)) return null
      const matchedPort = cwPortGeojson.features.find(
        (feature) => String(feature?.properties?.cw_id) === String(portId),
      )
      return matchedPort ? getPortLabel(matchedPort) : null
    },
    [cwPortGeojson, getPortLabel],
  )

  useEffect(() => {
    startEndpointRef.current = startEndpoint
  }, [startEndpoint])

  useEffect(() => {
    finishEndpointRef.current = finishEndpoint
  }, [finishEndpoint])

  useEffect(() => {
    if (switchManualPathing) {
      endpointSyncSourceRef.current = "inputs"
      const nextFeatures = []

      if (
        Array.isArray(startEndpoint?.coordinates) &&
        startEndpoint.coordinates.length === 2
      ) {
        nextFeatures.push(
          turf.point(startEndpoint.coordinates, {
            legtype: startLegtype,
            legType: startLegtype,
            type: "original_point",
            ...(startEndpoint?.portId == null
              ? {}
              : { portId: startEndpoint.portId }),
          }),
        )
      }

      nextFeatures.push(
        ...manualMiddles.map((feature) =>
          turf.point(feature.geometry.coordinates, {
            ...feature.properties,
            legtype:
              feature.properties?.legtype ??
              feature.properties?.legType ??
              "greatcircle",
            legType:
              feature.properties?.legType ??
              feature.properties?.legtype ??
              "greatcircle",
            type: feature.properties?.type ?? "original_point",
          }),
        ),
      )

      if (
        Array.isArray(finishEndpoint?.coordinates) &&
        finishEndpoint.coordinates.length === 2
      ) {
        nextFeatures.push(
          turf.point(finishEndpoint.coordinates, {
            legtype: "greatcircle",
            legType: "greatcircle",
            type: "original_point",
            ...(finishEndpoint?.portId == null
              ? {}
              : { portId: finishEndpoint.portId }),
          }),
        )
      }

      setVoyagePlannerPoints(
        turf.featureCollection(
          nextFeatures.map((feature, index) =>
            turf.point(feature.geometry.coordinates, {
              ...feature.properties,
              number: index + 1,
              extended_number: feature.properties?.extended_number ?? 0,
            }),
          ),
        ),
      )
      return
    }

    if (endpointSyncSourceRef.current === "points") {
      endpointSyncSourceRef.current = null
      return
    }

    const startCoords = startEndpoint?.coordinates
    const finishCoords = finishEndpoint?.coordinates

    endpointSyncSourceRef.current = "inputs"
    setVoyagePlannerPoints((prevPoints) => {
      if (
        !Array.isArray(startCoords) ||
        startCoords.length !== 2 ||
        !Array.isArray(finishCoords) ||
        finishCoords.length !== 2
      ) {
        return turf.featureCollection([])
      }

      const prevFeatures = [...(prevPoints?.features ?? [])]
      const middleFeatures =
        prevFeatures.length > 2 ? prevFeatures.slice(1, -1) : []

      const startProperties = {
        ...(prevFeatures[0]?.properties ?? {}),
        legtype: prevFeatures[0]?.properties?.legtype ?? "greatcircle",
        legType: prevFeatures[0]?.properties?.legType ?? "greatcircle",
        type: prevFeatures[0]?.properties?.type ?? "original_point",
      }
      if (startEndpoint?.portId == null) {
        delete startProperties.portId
      } else {
        startProperties.portId = startEndpoint.portId
      }

      const finishProperties = {
        ...(prevFeatures.at(-1)?.properties ?? {}),
        legtype: prevFeatures.at(-1)?.properties?.legtype ?? "greatcircle",
        legType: prevFeatures.at(-1)?.properties?.legType ?? "greatcircle",
        type: prevFeatures.at(-1)?.properties?.type ?? "original_point",
      }
      if (finishEndpoint?.portId == null) {
        delete finishProperties.portId
      } else {
        finishProperties.portId = finishEndpoint.portId
      }

      const nextFeatures = [
        turf.point(startCoords, startProperties),
        ...middleFeatures,
        turf.point(finishCoords, finishProperties),
      ].map((feature, index) =>
        turf.point(feature.geometry.coordinates, {
          ...feature.properties,
          number: index + 1,
          extended_number: feature.properties?.extended_number ?? 0,
        }),
      )

      return turf.featureCollection(nextFeatures)
    })
  }, [
    finishEndpoint,
    manualMiddles,
    startLegtype,
    setVoyagePlannerPoints,
    startEndpoint,
    switchManualPathing,
  ])

  // Sync input text from planner points when geometry changes outside the form, e.g. map edits.
  // Only a points change counts; late-loading ports must not clear a start without finish.
  const lastSyncedPointsRef = useRef(voyagePlannerPoints)
  useEffect(() => {
    if (lastSyncedPointsRef.current === voyagePlannerPoints) {
      return
    }
    lastSyncedPointsRef.current = voyagePlannerPoints

    if (endpointSyncSourceRef.current === "inputs") {
      endpointSyncSourceRef.current = null
      return
    }

    const points = voyagePlannerPoints?.features ?? []
    endpointSyncSourceRef.current = "points"
    if (points.length === 0) {
      setStartEndpoint(null)
      setFinishEndpoint(null)
      setStartInput("")
      setFinishInput("")
      return
    }

    const firstPoint = points[0]
    const first = firstPoint?.geometry?.coordinates
    const previousStart = startEndpointRef.current
    const previousFinish = finishEndpointRef.current

    if (points.length === 1 && Array.isArray(first) && first.length === 2) {
      const singlePointText =
        getPortDisplayLabelById(firstPoint?.properties?.portId) ??
        formatCoordPair(first[0], first[1])
      const singleEndpoint = buildEndpoint({
        lng: first[0],
        lat: first[1],
        portId: firstPoint?.properties?.portId,
        text: singlePointText,
      })

      const matchesPreviousStart = coordinatesMatch(
        first,
        previousStart?.coordinates,
      )
      const matchesPreviousFinish = coordinatesMatch(
        first,
        previousFinish?.coordinates,
      )

      if (previousStart && !previousFinish && !matchesPreviousStart) {
        setFinishEndpoint(singleEndpoint)
        setFinishInput(singlePointText)
        return
      }

      if (!previousStart && previousFinish && !matchesPreviousFinish) {
        setStartEndpoint(singleEndpoint)
        setStartInput(singlePointText)
        return
      }

      if (matchesPreviousStart && previousFinish) {
        setStartEndpoint(singleEndpoint)
        setStartInput(singlePointText)
        setFinishEndpoint(null)
        setFinishInput("")
        return
      }

      if (matchesPreviousFinish && previousStart) {
        setFinishEndpoint(singleEndpoint)
        setFinishInput(singlePointText)
        setStartEndpoint(null)
        setStartInput("")
        return
      }
    }

    if (Array.isArray(first) && first.length === 2) {
      const startLabel = getPortDisplayLabelById(firstPoint?.properties?.portId)
      const startText = startLabel ?? formatCoordPair(first[0], first[1])
      setStartEndpoint(
        buildEndpoint({
          lng: first[0],
          lat: first[1],
          portId: firstPoint?.properties?.portId,
          text: startText,
        }),
      )
      setStartInput(startText)
    }

    if (points.length === 1) {
      setFinishEndpoint(null)
      setFinishInput("")
      return
    }

    const lastPoint = points[points.length - 1]
    const last = lastPoint?.geometry?.coordinates
    if (Array.isArray(last) && last.length === 2) {
      const finishLabel = getPortDisplayLabelById(lastPoint?.properties?.portId)
      const finishText = finishLabel ?? formatCoordPair(last[0], last[1])
      setFinishEndpoint(
        buildEndpoint({
          lng: last[0],
          lat: last[1],
          portId: lastPoint?.properties?.portId,
          text: finishText,
        }),
      )
      setFinishInput(finishText)
    }
  }, [
    buildEndpoint,
    getPortDisplayLabelById,
    setFinishInput,
    setStartInput,
    voyagePlannerPoints,
  ])

  // Handler for vessel selection in the planner autocomplete.
  const handleVesselChange = (_e, value) => {
    setPlannerSelectedVessel(value)
    const nextVesselId = Number(value?.id)
    setMarkedVesselNames(
      Number.isFinite(nextVesselId)
        ? [
            {
              vesselId: nextVesselId,
              vesselName: value?.name ?? "",
            },
          ]
        : [],
    )
    if (Number.isFinite(nextVesselId)) {
      if (!switchManualPathing) setApplyRnwSafety(true)
      applyStartFromVesselPosition(nextVesselId)
      recenterToVesselLastPosition?.(nextVesselId)
    }
  }

  // Free-text input handlers for the endpoint autocompletes (lat,lon parsing).
  const handleStartInputChange = (event, newInputValue, reason) => {
    setStartInput(newInputValue)
    if (reason === "reset") return
    if (reason === "clear" || newInputValue === "") {
      setStartError(false)
      setStartEndpoint(null)
      return
    }
    if (reason !== "input") return
    const coords = parseCoordsInput(newInputValue)
    setStartError(coords == null)
    if (coords == null) return
    updateStartPoint(coords.lat, coords.lng, undefined, newInputValue)
  }

  const handleFinishInputChange = (event, newInputValue, reason) => {
    setFinishInput(newInputValue)
    if (reason === "reset") return
    if (reason === "clear" || newInputValue === "") {
      setFinishError(false)
      setFinishEndpoint(null)
      return
    }
    if (reason !== "input") return
    const coords = parseCoordsInput(newInputValue)
    setFinishError(coords == null)
    if (coords == null) return
    updateFinishPoint(coords.lat, coords.lng, undefined, newInputValue)
  }

  // Handler for when a start port is selected from the autocomplete options.
  const handleStartOptionSelect = (event, newValue) => {
    if (typeof newValue === "object" && newValue !== null) {
      // Set the input value to the port label (e.g. "Rhodes (GR)")
      const nextText = getPortLabel(newValue)
      setStartInput(nextText)
      setStartError(false)
      // Update the voyage planner with the port's coordinates (GeoJSON uses [lng, lat])
      const [lng, lat] = newValue.geometry.coordinates
      const portId = newValue.properties.cw_id
      updateStartPoint(lat, lng, portId, nextText)
    }
  }

  // Handler for when a finish port is selected from the autocomplete options.
  const handleFinishOptionSelect = (event, newValue) => {
    if (typeof newValue === "object" && newValue !== null) {
      // Set the finish input to the port label (e.g. "Rhodes (GR)")
      const nextText = getPortLabel(newValue)
      setFinishInput(nextText)
      setFinishError(false)
      const [lng, lat] = newValue.geometry.coordinates
      updateFinishPoint(lat, lng, newValue.properties.cw_id, nextText)
    }
  }

  // Handler for when the departure time is changed.
  const handleDateChange = (newValue) => {
    if (!newValue) {
      setCalculatorDepartureTime(newValue)
      return
    }
    setCalculatorDepartureTime(dayjs(newValue).utc())
  }

  // Handler for base speed input
  const handleBasespeedInputChange = (event) => {
    // Normalize comma to dot for decimal parsing and allow typing a dot
    const value = event.target.value.replace(/,/g, ".")
    // Require at least one digit before optional dot
    const regex = /^\d+(\.\d*)?$/
    // Exclude empty, lone dot, or values that are all zeros (e.g. "0", "0.", "0.0", "00", etc.)
    const isAllZeros = /^0+(\.0*)?$/.test(value)
    if (value === "" || value === "." || isAllZeros) {
      setCalculatorBasespeed(value)
      setBasespeedError(true)
    } else if (regex.test(value)) {
      setCalculatorBasespeed(value)
      setBasespeedError(false)
    } else {
      setCalculatorBasespeed(value)
      setBasespeedError(true)
    }
  }

  // Handlers for closing snackbars
  const handleEtaErrorClose = (event, reason) => {
    if (reason === "clickaway") return
    setEtaError(null)
  }

  // Handler for when the "Pathfinder" button is clicked. Accepts an optional
  // settings override so a commit-and-compute in the same tick doesn't read
  // stale state.
  async function handlePathfinderButtonClick(settingsOverride) {
    // Fill the vessel master data from the real vessel meta — the settings
    // copy starts empty (no default vessel).
    const effectiveSettings = mergeVesselMetaIntoSettings(
      settingsOverride ?? plannerVesselSettings,
      specVesselMeta,
    )
    clearRouteError()
    // Ensure valid token
    const accessToken = await checkAndRefreshToken(
      accessContext,
      setAccessContext,
    )
    if (!accessToken) {
      setLoadingCalcButton(false)
      return
    }
    setLoadingCalcButton(true)
    setVoyageActive(false)
    const { draftRouteId, draftContext } = buildDraftTarget()
    try {
      await Pathfinder({
        draftRouteId,
        draftContext,
        map,
        accessContext,
        setAccessContext,
        voyagePlannerPoints,
        calculatorDepartureTime,
        calculatorBasespeed,
        setLoadingPathfinder: setLoadingCalcButton,
        setEtaResponse,
        speedProfileData,
        consumptionProfileData,
        ...fuelParams,
        vesselId: specVesselId,
        setRouteConsumption,
        windLimit: effectiveSettings.windLimit,
        waveLimit: effectiveSettings.waveLimit,
        currentLimit: effectiveSettings.currentLimit,
        cwViaAreasGeojson,
        plannerVesselSettings: effectiveSettings,
        setPathfinderResponse,
        setRouteError,
        applyRnwSafety,
        applyEca,
        avoidHra,
        avoidJwla,
        avoidCaa,
        erklaerbaer: canSeeErklaerbaer && erklaerbaerEnabled,
        erklaerbaerId,
        setErklaerbaerId,
        replaceRoute,
        vesselName: draftVesselLabel,
      })
    } finally {
      setLoadingCalcButton(false)
    }
  }

  // Handler for when the "ETA" button is clicked
  async function handleETAButtonClick(settingsOverride) {
    // Fill the vessel master data from the real vessel meta — the settings
    // copy starts empty (no default vessel).
    const effectiveSettings = mergeVesselMetaIntoSettings(
      settingsOverride ?? plannerVesselSettings,
      specVesselMeta,
    )
    setEtaError(null)
    clearRouteError()
    // Ensure valid token
    const accessToken = await checkAndRefreshToken(
      accessContext,
      setAccessContext,
    )
    if (!accessToken) {
      setLoadingCalcButton(false)
      return
    }
    setLoadingCalcButton(true)
    setVoyageActive(false)
    const { draftRouteId, draftContext } = buildDraftTarget()
    try {
      await runPlanrouteAnalyser({
        draftRouteId,
        draftContext,
        source: "planner",
        accessContext,
        setAccessContext,
        voyagePlannerPoints,
        calculatorDepartureTime,
        calculatorBasespeed,
        plannerVesselSettings: effectiveSettings,
        speedProfileData,
        consumptionProfileData,
        ...fuelParams,
        vesselId: specVesselId,
        windLimit: effectiveSettings?.windLimit,
        waveLimit: effectiveSettings?.waveLimit,
        currentLimit: effectiveSettings?.currentLimit,
        cwViaAreasGeojson,
        ...restrictionFlags,
        setLoadingRequest: setLoadingCalcButton,
        setEtaResponse,
        setRouteConsumption,
        setPathfinderResponse,
        setEtaError,
        setRouteError,
        replaceRoute,
        setIsCalculating,
        vesselName: draftVesselLabel,
      })
    } finally {
      setLoadingCalcButton(false)
    }
  }

  // buildFuelRequestParams owns the "fuel needs a price" gate. Missing
  // prices don't disable the Fuel pill — they block Find Route via
  // missingRequirements, like an invalid departure time.
  const fuelParams = buildFuelRequestParams({
    bunkerPrices,
    currentDraft,
    fuelOptimisation: plannerRoutingMode === "fuel",
    consumptionProfile: consumptionProfileData?.values,
  })

  // Manual checks ECA and all zones; RNW stays untouched (masked) — changing it would invalidate the draft.
  const routedFlagsRef = useRef(null)
  const setRestrictionSwitches = useCallback(
    ({ eca, hra, jwla, caa }) => {
      setApplyEca(eca)
      setAvoidHra(hra)
      setAvoidJwla(jwla)
      setAvoidCaa(caa)
    },
    [setApplyEca, setAvoidHra, setAvoidJwla, setAvoidCaa],
  )

  const handleRoutingModeChange = useCallback(
    (mode) => {
      const wasManual = plannerRoutingMode === "manual"
      setPlannerRoutingMode(mode)
      if (mode === "manual" && !wasManual) {
        routedFlagsRef.current = {
          eca: applyEca,
          hra: avoidHra,
          jwla: avoidJwla,
          caa: avoidCaa,
        }
        setRestrictionSwitches({ eca: true, hra: true, jwla: true, caa: true })
        return
      }
      if (mode !== "manual" && wasManual && routedFlagsRef.current) {
        setRestrictionSwitches(routedFlagsRef.current)
        routedFlagsRef.current = null
      }
      if (mode === "fuel") setApplyEca(true)
      if (mode !== "manual" && wasManual) {
        // Waypoints stay with a computed manual draft; time/fuel only route between the endpoints.
        discardSketch()
        setVoyagePlannerPoints((prev) => {
          const features = prev?.features ?? []
          return features.length > 2
            ? turf.featureCollection([features[0], features.at(-1)])
            : prev
        })
      }
    },
    [
      plannerRoutingMode,
      applyEca,
      avoidHra,
      avoidJwla,
      avoidCaa,
      setRestrictionSwitches,
      setApplyEca,
      discardSketch,
      setPlannerRoutingMode,
      setVoyagePlannerPoints,
    ],
  )

  const handleResetClick = () => {
    setVoyagePlannerPoints(turf.featureCollection([]))
    setStartEndpoint(null)
    setFinishEndpoint(null)
    setStartInput("")
    setFinishInput("")
    setStartError(false)
    setFinishError(false)
    setEtaResponse(false)
    setRouteConsumption(false)
    if (plannerDraftRouteId != null) {
      replaceRoute(plannerDraftRouteId, createDraftDiscardState(plannerDraftRouteId))
    }
    setSelectedProfile(profileOptions[0])
    setPlannerSelectedVessel(null)
    autoFilledStartVesselIdRef.current = null
    lastFallbackFillRef.current = null
    lastArrivalFillRef.current = null
    setStartFallbackRequest(null)
    setArrivalRequest(null)
    setLoadingCalcButton(false)
    setPathfinderResponse(null)
    goToStep(0)
  }

  // The Options step edits a draft copy of the planner settings; leaving the
  // step validates and commits it (same semantics as the old modal's Apply).
  const commitOptionsIfValid = () => {
    const next = commitIfValid()
    if (next == null) setOptionsBlockedOpen(true)
    return next
  }

  const goToWizardStep = (step) => {
    if (step === wizardStep) return
    if (wizardStep === 1 && commitOptionsIfValid() == null) return
    if (step === 1) enterSpecs()
    goToStep(step)
  }

  // Every open starts on the Plan step; invalid Options edits keep the user there.
  const wasOpenRef = useRef(isOpen)
  useEffect(() => {
    const opened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    if (opened) goToWizardStep(0)
  }, [isOpen])

  const handleComputeClick = () => {
    const committedSettings = commitOptionsIfValid()
    if (committedSettings == null) return
    const run = switchManualPathing
      ? handleETAButtonClick
      : handlePathfinderButtonClick
    // Jump to the result step right away — it shows the spinner while the
    // computation runs.
    goToStep(2)
    run(committedSettings)
  }

  const routeErrorContext = useMemo(() => {
    const endpointSummary = (endpoint) => {
      const [lng, lat] = endpoint?.coordinates ?? []
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null
      const port = (cwPortGeojson?.features ?? []).find(
        (f) => endpoint.portId != null && String(f?.properties?.cw_id) === String(endpoint.portId),
      )
      return {
        name: port?.properties?.name ?? "Custom position",
        coords: formatCoordPair(lng, lat),
        latlng: [lat, lng],
      }
    }
    const departure = endpointSummary(startEndpoint)
    const arrival = endpointSummary(finishEndpoint)
    return {
      departure,
      arrival,
      departureTime: calculatorDepartureTime?.isValid?.()
        ? dayjs.utc(calculatorDepartureTime).format("DD MMM YYYY · HH:mm [UTC]")
        : null,
      speedLabel: calculatorBasespeed
        ? `${calculatorBasespeed} kn · ${applyRnwSafety ? "RNW-filtered" : "Weather-filtered"}`
        : null,
      onShowOnMap: recenterToPoint
        ? (target) => {
            const latlng = (target === "arrival" ? arrival : departure)?.latlng
            if (latlng) recenterToPoint(latlng)
          }
        : null,
    }
  }, [
    startEndpoint,
    finishEndpoint,
    cwPortGeojson,
    calculatorDepartureTime,
    calculatorBasespeed,
    applyRnwSafety,
    recenterToPoint,
  ])

  const errorAdjustStep = routeError ? routeErrorAdjustStep(routeError.kind) : null
  const footerProps =
    wizardStep === 0
      ? {
          secondaryLabel: "Reset",
          onSecondary: handleResetClick,
          primaryLabel: "Continue to Options",
          onPrimary: () => goToWizardStep(1),
        }
      : wizardStep === 1
        ? {
            secondaryLabel: "Back",
            onSecondary: () => goToWizardStep(0),
            primaryLabel: switchManualPathing ? "Calculate ETA" : "Find Route",
            onPrimary: handleComputeClick,
            primaryDisabled: !isButtonEnabled,
            primaryLoading: loadingCalcButton,
            primaryHints: isButtonEnabled ? null : missingRequirements,
          }
        : routeError
          ? {
              secondaryLabel: "Back",
              onSecondary: () => goToWizardStep(1),
              primaryLabel: errorAdjustStep == null ? "Retry" : "Adjust inputs",
              onPrimary:
                errorAdjustStep == null
                  ? handleComputeClick
                  : () => goToWizardStep(errorAdjustStep),
              primaryLoading: loadingCalcButton,
            }
          : {
              secondaryLabel: "Back",
              onSecondary: () => goToWizardStep(1),
              primaryLabel: "Show in Fleet Explorer",
              onPrimary: () => {
                onGoToVesselPanel?.(
                  plannerSelectedVessel?.name,
                  plannerSelectedVessel?.id,
                  draftVoyage?.id ?? null,
                )
              },
              primaryDisabled:
                !draftHasPoints || isSketch || isCalculating || loadingCalcButton,
              primaryColor: "#2e7d32",
            }

  // Do not unmount panel, just hide via CSS

  return (
    <>
      <Paper
        key="voyage-planner-panel"
        id="voyage-planner-panel"
        data-tour="planner-panel"
        ref={panelRef}
        elevation={0}
        sx={{
          display: isOpen ? "flex" : "none",
          flexDirection: "column",
          position: "absolute",
          top: bounds.top,
          zIndex: 1000,
          bgcolor: "background.paper",
          width: `${panelWidth}px`,
          color: "#004f7d",
          borderRadius: "0px",
          marginLeft: `${bounds.left}px`,
          overflow: "hidden",
          height: `${panelMaxHeight}px`,
          boxSizing: "border-box",
          p: 1.5,
          // pb:0
        }}
        onMouseEnter={() => {
          onMouseEnter(map)
          setVoyageActive(false)
        }}
        onMouseLeave={() => {
          onMouseLeave(map)
          setVoyageActive(true)
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Box>
            <Typography sx={PANEL_TITLE_SX}>Voyage Planner</Typography>
            <Typography sx={PANEL_SUBTITLE_SX}>Planning workspace</Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TourHelpButton tourId={plannerRoutingMode === "manual" ? "manual-route" : "plan-route"} />
            <VisibilitySwitch
              checked={switchVoyagePlannerActive}
              onChange={(event) => {
                const isChecked = event.target.checked
                setSwitchVoyagePlannerActive(isChecked)
              }}
              slotProps={{ input: { "aria-label": "toggle voyage planner visibility" } }}
            />

            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: "#004f7d" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <WizardStepNav activeStep={wizardStep} onStepClick={goToWizardStep} />
        <Divider sx={PANEL_HEADER_DIVIDER_SX} />

        <Box
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            overflowX: "hidden",
            overscrollBehavior: "contain",
            ...PANEL_SCROLLBAR_SX,
          }}
        >
        {wizardStep === 0 && (
          <PlanStep
            plannerVesselOptions={plannerVesselOptions}
            plannerSelectedVessel={plannerSelectedVessel}
            onVesselChange={handleVesselChange}
            hasSelectedVessel={hasSelectedVessel}
            plannerRoutingMode={plannerRoutingMode}
            onRoutingModeChange={handleRoutingModeChange}
            plannerSelectedVoyage={plannerSelectedVoyage}
            voyageChipMode={
              startsNewVoyage
                ? "new"
                : plannerVoyagesFailed && !plannerSelectedVoyage
                  ? "unavailable"
                  : "existing"
            }
            portOptions={portOptions}
            getPortLabel={getPortLabel}
            startAutoOpen={startAutoOpen}
            onStartAutoOpenChange={setStartAutoOpen}
            finishAutoOpen={finishAutoOpen}
            onFinishAutoOpenChange={setFinishAutoOpen}
            startInput={startInput}
            startError={startError}
            onStartInputChange={handleStartInputChange}
            onStartOptionSelect={handleStartOptionSelect}
            finishInput={finishInput}
            finishError={finishError}
            onFinishInputChange={handleFinishInputChange}
            onFinishOptionSelect={handleFinishOptionSelect}
            hasVesselLastPosition={hasVesselLastPosition}
            hasVessel={Number.isFinite(Number(plannerSelectedVessel?.id))}
            onUseVesselPosition={() =>
              applyStartFromVesselPosition(plannerSelectedVessel?.id)
            }
            canUseVoyageDestination={
              (activeRoutePoints?.features?.length ?? 0) > 0
            }
            onUseVoyageDestination={applyArrivalFromRoute}
            calculatorDepartureTime={calculatorDepartureTime}
            onDateChange={handleDateChange}
            calculatorBasespeed={calculatorBasespeed}
            basespeedError={basespeedError}
            onBasespeedChange={handleBasespeedInputChange}
            plannerVesselSettings={mergedSettings}
            currentDraft={currentDraft}
            consumptionProfileData={consumptionProfileData}
            onEditSpecifications={() => goToWizardStep(1)}
            {...restrictionFlags}
            onApplyRnwSafetyChange={setApplyRnwSafety}
            onApplyEcaChange={setApplyEca}
            onAvoidHraChange={setAvoidHra}
            onAvoidJwlaChange={setAvoidJwla}
            onAvoidCaaChange={setAvoidCaa}
            canSeeErklaerbaer={canSeeErklaerbaer}
            erklaerbaerEnabled={erklaerbaerEnabled}
            onErklaerbaerChange={setErklaerbaerEnabled}
          />
        )}

        {wizardStep === 1 && (
          <OptionsStep
            specTab={specTab}
            onSpecTabChange={setSpecTab}
            draftPlannerVesselSettings={draftPlannerVesselSettings}
            setDraftPlannerVesselSettings={setDraftPlannerVesselSettings}
            optionsErrors={optionsErrors}
            setOptionsError={setOptionsError}
            vesselLabel={
              selectedProfile?.vesselName ?? plannerSelectedVessel?.name ?? ""
            }
            vesselMeta={specVesselMeta}
            vesselId={specVesselId}
            speedProfileData={speedProfileData}
            consumptionProfileData={consumptionProfileData}
            calculatorBasespeed={calculatorBasespeed}
            defaultPlannerVesselSettings={defaultPlannerVesselSettings}
            bunkerPrices={bunkerPrices}
            setBunkerPrices={setBunkerPrices}
            currentDraft={currentDraft}
            setCurrentDraft={setCurrentDraft}
            routingInactive={switchManualPathing}
          />
        )}

        {wizardStep === 2 && (
          <ResultStep
            isLoading={loadingCalcButton || isCalculating}
            routeSummary={routeSummary}
            etaResponse={etaResponse}
            routeConsumption={routeConsumption}
            pathfinderResponse={pathfinderResponse}
            {...restrictionFlags}
            waveLimit={mergedSettings.waveLimit}
            windLimit={mergedSettings.windLimit}
            currentLimit={mergedSettings.currentLimit}
            consumptionProfileData={consumptionProfileData}
            calculatorBasespeed={calculatorBasespeed}
            routeError={routeError}
            routeErrorContext={routeErrorContext}
            etaError={etaError}
            routeOutdated={switchManualPathing && isSketch}
            onGoToOptions={() => goToWizardStep(1)}
          />
        )}
        </Box>

        <WizardFooter {...footerProps} />
      </Paper>

      <PanelResizeHandle
        visible={isOpen}
        panelWidth={panelWidth}
        panelMaxHeight={panelMaxHeight}
        onMouseDown={handleResizeMouseDown}
      />

      <NotificationSnackbar
        open={routeErrorToastOpen}
        message={routeError?.message}
        onClose={closeRouteErrorToast}
      />

      <NotificationSnackbar
        open={optionsBlockedOpen}
        severity="warning"
        message="Please fix the invalid specification values first"
        autoHideDuration={3000}
        onClose={() => setOptionsBlockedOpen(false)}
        showCloseButton={false}
      />

      <NotificationSnackbar
        open={!!etaError}
        message={etaError}
        onClose={handleEtaErrorClose}
      />

      <Portal>
        <Snackbar
          open={erklaerbaerBlockedOpen && !!erklaerbaerId}
          onClose={(event, reason) => {
            if (reason === "clickaway") return
            setErklaerbaerBlockedOpen(false)
          }}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          style={{ zIndex: 1500 }}
          slotProps={{
            content: {
              sx: {
                bgcolor: "#004f7d",
                color: "#fff",
                borderRadius: "12px",
                boxShadow: "0 4px 16px rgba(0, 79, 125, 0.35)",
              },
            },
          }}
          message="Pop-up blocked — route analysis report is ready"
          action={
            <>
              <Button
                size="small"
                onClick={handleOpenBlockedErklaerbaer}
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  textTransform: "none",
                  bgcolor: "rgba(255, 255, 255, 0.14)",
                  borderRadius: "10px",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.26)" },
                }}
              >
                Open
              </Button>
              <IconButton
                size="small"
                color="inherit"
                aria-label="close"
                onClick={() => setErklaerbaerBlockedOpen(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </>
          }
        />
      </Portal>
    </>
  )
}

export default React.memo(VoyagePlannerPanel)
